import * as z from "@-/com.npmjs.zod";

export const graphemeLessonPartSchema = z.enum([
  "primitive",
  "meaning",
  "meaning-and-pronunciation",
  "pronunciation",
]);

export type GraphemeLessonPart = z.infer<typeof graphemeLessonPartSchema>;

export const levelSchema = z.object({
  /**
   * Each of `characters`' own curriculum grade, by position: the HSK level (TBCL level) or standard
   * chart milestone that lists it, in `curriculumGrade` numbering. A higher-grade prerequisite keeps
   * its own grade here, unlike the level's `curriculumGrade`. `null` for a component outside the
   * dataset (a stroke, radical or primitive).
   */
  characterGrades: z.array(z.number().nullable()).optional(),
  characters: z.array(z.string()),
  /** Bundled teaching intent for each glyph; independent of runtime dictionary/card metadata. */
  lessonParts: z.record(z.string(), graphemeLessonPartSchema).optional(),
  /**
   * Grade 0 is the authored Ready pack. Positive grades belong to the generated curriculum built
   * on top of it, including its prerequisite characters (HSK or TBCL). For HSK, 7 means HSK 7–9
   * and 8–10 are the PRC standard-character Tier 1–3 milestones; for TBCL, 8–10 are the Taiwan MOE
   * chart A–C milestones. Browse → Levels groups these into material bands (Starter → Zenith, or
   * Starter → Completionist; see `HSK_MATERIAL_BANDS` / `TBCL_MATERIAL_BANDS`).
   */
  curriculumGrade: z.number().optional(),
  description: z.string(),
  done: z.boolean(),
  extra: z.array(z.string()),
  index: z.number(),
  introduces: z.array(z.string()),
  kangxi: z.array(z.number()),
  /**
   * The level introduces its characters' meanings only (the hardcoded simplified opening). Their
   * pronunciations are introduced later, by the pronunciation level of the last meaning-only level
   * (`pronunciationCharacters`).
   */
  meaningOnly: z.boolean().optional(),
  /**
   * Optional curator display name from the level file's YAML `name`. When set, Browse appends it
   * after the numeric label (`Level N {name}`).
   */
  name: z.string().optional(),
  /**
   * Optional curator display name of the pronunciation level that follows (its `NNN-Pk` level
   * file's YAML `name`). When set, Browse appends it after the numeric label
   * (`Level N-Pk {name}`).
   */
  nextup: z.string(),
  pronunciationName: z.string().optional(),
  /**
   * The pronunciation level that follows this level (labelled `{N}-P{k}`, see
   * `formatPronunciationLevelLabel`). Either a hardcoded list from
   * `HARDCODED_PRONUNCIATION_LEVELS` (exact slots, any character level) or, for a meaning-only run
   * with no hardcoded override, that run's pronounceable characters in teaching order.
   */
  pronunciationCharacters: z.array(z.string()).optional(),
  /** Word lessons authored at the end of this pronunciation level; generated words stay separate. */
  pronunciationWords: z.array(z.string()).optional(),
  slug: z.string().nullable(),
  /**
   * Ordered lesson groups (glyphs that must not be separated by reviews). Formerly called "units".
   * See `lib/lesson/TERMINOLOGY.md`.
   */
  lessonGroups: z.array(z.array(z.string())).optional(),
});

export type Level = z.infer<typeof levelSchema>;
